import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import { color, shadow } from '../theme/tokens'
import { Logo } from './Logo'

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', to: null, icon: DashboardIcon },
  { key: 'phone', label: 'Phone', to: null, icon: PhoneIcon },
  { key: 'chat', label: 'Chat', to: null, icon: ChatIcon },
  { key: 'email', label: 'Email', to: null, icon: MailIcon },
  { key: 'whatsapp', label: 'WhatsApp', to: null, icon: WhatsAppIcon },
  { key: 'acp', label: 'ACP', to: '/console', icon: AgentIcon },
  { key: 'relatorios', label: 'Relatórios', to: null, icon: ChartIcon },
]

export function SideNav() {
  return (
    <nav
      aria-label="Módulos"
      style={{
        width: 60,
        background: color.panel,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 6,
        boxShadow: shadow.nav,
        zIndex: 20,
      }}
    >
      {NAV_ITEMS.map(({ key, label, to, icon: Icon }) => {
        const active = key === 'acp'
        const content = (
          <>
            <Icon stroke={active ? color.panel : '#fff'} />
            {label}
          </>
        )
        const style = {
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          gap: 4,
          padding: active ? '10px 4px' : '9px 4px',
          background: active ? color.brandTintStrong : 'transparent',
          color: active ? color.panel : '#fff',
          fontSize: 10.5,
          fontWeight: active ? 600 : 500,
          cursor: 'pointer',
          textDecoration: 'none',
          textAlign: 'center' as const,
        }

        return to ? (
          <NavLink key={key} to={to} style={style}>
            {content}
          </NavLink>
        ) : (
          <div
            key={key}
            style={{ ...style, opacity: 0.75 }}
            title={`${label} — fora do escopo do MVP`}
          >
            {content}
          </div>
        )
      })}
    </nav>
  )
}

export function Header({
  subtitle,
  badge,
  children,
}: {
  subtitle: string
  badge?: string
  children?: ReactNode
}) {
  return (
    <header
      style={{
        background: color.panel,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 20px',
        height: 56,
        flex: 'none',
        zIndex: 40,
        boxShadow: shadow.header,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '-.2px',
        }}
      >
        <Logo />
        <span>
          Hub <span style={{ fontWeight: 800 }}>de Cobrança</span>
        </span>
      </div>
      <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,.16)' }} />
      <span style={{ fontSize: 13, fontWeight: 500, color: color.panelText }}>{subtitle}</span>
      {badge ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: color.panelTextMuted,
            border: '1px dashed rgba(255,255,255,.22)',
            borderRadius: 999,
            padding: '4px 10px',
          }}
        >
          {badge}
        </span>
      ) : null}
      <div style={{ flex: 1 }} />
      {children}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: color.brand,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
          flex: 'none',
        }}
        title="M. Alvarenga"
      >
        M
      </div>
    </header>
  )
}

// --------------------------------------------------------------------- ícones --

type IconProps = { stroke?: string }

function base(stroke = '#fff') {
  return {
    width: 20,
    height: 20,
    fill: 'none',
    stroke,
    strokeWidth: 1.6,
  } as const
}

function DashboardIcon({ stroke }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" style={base(stroke)} aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  )
}

function PhoneIcon({ stroke }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" style={{ ...base(stroke), strokeLinecap: 'round' }} aria-hidden>
      <path d="M15.7 12.6c-.5-.2-1.1-.1-1.5.3l-1 1.3a13 13 0 0 1-4.5-4.5l1.3-1a1.3 1.3 0 0 0 .4-1.5L8.9 4.8a1.3 1.3 0 0 0-1.4-.8L4.5 4.7A1.3 1.3 0 0 0 3.5 6C3.5 13.4 9.2 19.5 16.5 20a1.3 1.3 0 0 0 1.4-1l.6-3a1.3 1.3 0 0 0-.8-1.5l-1.9-.9Z" />
    </svg>
  )
}

function ChatIcon({ stroke }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" style={base(stroke)} aria-hidden>
      <path d="M21 11.5a8.4 8.4 0 0 1-8.9 8.4 8.7 8.7 0 0 1-3.6-.8L3 20l1-5.2A8.4 8.4 0 1 1 21 11.5z" />
    </svg>
  )
}

function MailIcon({ stroke }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" style={base(stroke)} aria-hidden>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m3 6 9 7 9-7" />
    </svg>
  )
}

function WhatsAppIcon({ stroke }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" style={base(stroke)} aria-hidden>
      <path d="M21 11.5a8.4 8.4 0 0 1-8.9 8.4 8.7 8.7 0 0 1-3.6-.8L3 20l1-5.2A8.4 8.4 0 1 1 21 11.5z" />
      <path d="M8.5 9.5c0 3 2.5 5.5 5.5 5.5" strokeWidth="1.3" />
    </svg>
  )
}

function AgentIcon({ stroke }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" style={base(stroke)} aria-hidden>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M4.5 20c0-3.5 3.3-6 7.5-6s7.5 2.5 7.5 6" />
      <path d="M5 13v-1a7 7 0 0 1 14 0v1" />
    </svg>
  )
}

function ChartIcon({ stroke }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" style={base(stroke)} aria-hidden>
      <path d="M4 20V10M10 20V5M16 20v-7M22 20H2" />
    </svg>
  )
}
